import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'api/client';
import useAuthStore from 'stores/authStore';

export const settingsKeys = {
  tenant: ['settings-tenant'],
  members: ['settings-members'],
};

export function useTenantSettings() {
  return useQuery({
    queryKey: settingsKeys.tenant,
    queryFn: () => apiClient.get('/api/v1/auth/settings/').then((r) => r.data),
  });
}

export function useUpdateTenantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.patch('/api/v1/auth/settings/', data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: settingsKeys.tenant });

      // Mirror name + slug into the auth store: components render the tenant
      // from activeMembership.tenant, not from this query.
      const store = useAuthStore.getState();
      const am = store.activeMembership;
      if (am) {
        store.setActiveMembership({
          ...am,
          tenant: {
            ...am.tenant,
            name: updated.name ?? am.tenant.name,
            slug: updated.slug ?? am.tenant.slug,
          },
        });
      }
      // Also update the memberships list so the switcher stays consistent
      store.setMemberships(
        store.memberships.map((m) =>
          m.tenant.slug === am?.tenant.slug
            ? { ...m, tenant: { ...m.tenant, name: updated.name ?? m.tenant.name, slug: updated.slug ?? m.tenant.slug } }
            : m
        )
      );
    },
  });
}

export function useMembers() {
  return useQuery({
    queryKey: settingsKeys.members,
    queryFn: () => apiClient.get('/api/v1/auth/settings/members/').then((r) => r.data),
  });
}

export function useUpdateMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }) =>
      apiClient.patch(`/api/v1/auth/settings/members/${id}/`, { role }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.members }),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/api/v1/auth/settings/members/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.members }),
  });
}
